import { useState, useEffect, useCallback } from "react";
import { buildApiUrl } from "../config/api";

interface AdminStats {
  totalUsers: number;
  totalSpecs: number;
  totalApiKeys: number;
}

interface User {
  id: number;
  username: string;
  email: string | null;
  created_at: string;
  email_verified: number;
  oauth_provider: string | null;
}

interface Spec {
  id: string;
  user_id: number;
  email: string | null;
  username: string | null;
  created_at: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface AdminPanelProps {
  adminKey: string;
  onClose: () => void;
}

export function AdminPanel({ adminKey, onClose }: AdminPanelProps) {
  const [activeTab, setActiveTab] = useState<"stats" | "users" | "specs">("stats");
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [specs, setSpecs] = useState<Spec[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const headers = {
    "x-admin-key": adminKey,
  };

  // Wrap fetchData in useCallback to prevent infinite loops
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (activeTab === "stats") {
        const res = await fetch(buildApiUrl("/api/admin/stats"), { headers });
        if (!res.ok) throw new Error("Failed to fetch stats");
        const data = await res.json();
        setStats(data.stats);
      } else if (activeTab === "users") {
        const page = pagination.page;
        const limit = pagination.limit;
        const res = await fetch(buildApiUrl(`/api/admin/users?page=${page}&limit=${limit}`), { headers });
        if (!res.ok) throw new Error("Failed to fetch users");
        const data = await res.json();
        setUsers(data.users);
        setPagination(data.pagination);
      } else if (activeTab === "specs") {
        const page = pagination.page;
        const limit = pagination.limit;
        const res = await fetch(buildApiUrl(`/api/admin/specs?page=${page}&limit=${limit}`), { headers });
        if (!res.ok) throw new Error("Failed to fetch specs");
        const data = await res.json();
        setSpecs(data.specs);
        setPagination(data.pagination);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [activeTab, pagination.page, pagination.limit, adminKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;
    
    const { page, totalPages } = pagination;
    
    return (
      <div className="flex items-center justify-center gap-2 mt-4">
        <button
          onClick={() => handlePageChange(page - 1)}
          disabled={page <= 1}
          className="px-3 py-1 text-sm rounded border border-zinc-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-zinc-50"
        >
          Previous
        </button>
        <span className="text-sm text-zinc-600">
          Page {page} of {totalPages} ({pagination.total} total)
        </span>
        <button
          onClick={() => handlePageChange(page + 1)}
          disabled={page >= totalPages}
          className="px-3 py-1 text-sm rounded border border-zinc-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-zinc-50"
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-zinc-900 text-white px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-zinc-700 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold">Admin Panel</h2>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-zinc-200 px-6 shrink-0">
          <div className="flex gap-1">
            {(["stats", "users", "specs"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className={`px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? "text-zinc-900 border-b-2 border-zinc-900"
                    : "text-zinc-500 hover:text-zinc-700"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-zinc-200 border-t-zinc-900 rounded-full animate-spin"></div>
            </div>
          ) : error ? (
            <div className="text-red-500 text-center py-8">{error}</div>
          ) : activeTab === "stats" && stats ? (
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-zinc-50 rounded-lg p-6 text-center">
                <div className="text-4xl font-bold text-zinc-900">{stats.totalUsers.toLocaleString()}</div>
                <div className="text-sm text-zinc-500 mt-1">Total Users</div>
              </div>
              <div className="bg-zinc-50 rounded-lg p-6 text-center">
                <div className="text-4xl font-bold text-zinc-900">{stats.totalSpecs.toLocaleString()}</div>
                <div className="text-sm text-zinc-500 mt-1">Total Specs</div>
              </div>
              <div className="bg-zinc-50 rounded-lg p-6 text-center">
                <div className="text-4xl font-bold text-zinc-900">{stats.totalApiKeys.toLocaleString()}</div>
                <div className="text-sm text-zinc-500 mt-1">API Keys</div>
              </div>
            </div>
          ) : activeTab === "users" ? (
            <div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-200">
                      <th className="text-left py-3 px-3 text-xs font-medium text-zinc-500 uppercase">ID</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-zinc-500 uppercase">Username</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-zinc-500 uppercase">Email</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-zinc-500 uppercase">Provider</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-zinc-500 uppercase">Verified</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-zinc-500 uppercase">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                        <td className="py-3 px-3 text-sm text-zinc-900 font-mono">{user.id}</td>
                        <td className="py-3 px-3 text-sm text-zinc-900 font-medium">{user.username || '-'}</td>
                        <td className="py-3 px-3 text-sm text-zinc-600">{user.email || '-'}</td>
                        <td className="py-3 px-3 text-sm text-zinc-600">
                          {user.oauth_provider ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-zinc-100 text-zinc-800">
                              {user.oauth_provider}
                            </span>
                          ) : (
                            <span className="text-zinc-400">-</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-sm">
                          {user.email_verified ? (
                            <span className="inline-flex items-center text-green-600">✓</span>
                          ) : (
                            <span className="text-zinc-400">-</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-sm text-zinc-500">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {users.length === 0 && (
                  <p className="text-center text-zinc-500 py-8">No users found</p>
                )}
              </div>
              {renderPagination()}
            </div>
          ) : activeTab === "specs" ? (
            <div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-200">
                      <th className="text-left py-3 px-3 text-xs font-medium text-zinc-500 uppercase">Spec ID</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-zinc-500 uppercase">Owner</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-zinc-500 uppercase">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {specs.map((spec) => (
                      <tr key={spec.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                        <td className="py-3 px-3 text-sm text-zinc-900 font-mono">{spec.id}</td>
                        <td className="py-3 px-3 text-sm text-zinc-600">
                          {spec.username || spec.email || `User ${spec.user_id}`}
                        </td>
                        <td className="py-3 px-3 text-sm text-zinc-500">
                          {new Date(spec.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {specs.length === 0 && (
                  <p className="text-center text-zinc-500 py-8">No specs found</p>
                )}
              </div>
              {renderPagination()}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
